/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  type JSX,
  useEffect,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  type RouteComponentProps,
  withRouter
} from 'react-router-dom';

import { CheckCircle } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Typography
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import styled from 'styled-components';

import {
  Dialog,
  FormCard,
  Header,
  SplashLoader
} from '../../components';
import InfoTooltip from '../../components/InfoTooltip';
import { logger } from '../../features/logger';
import { withAPIService } from '../../networking';
import {
  errorDialogNotFound,
  errorDialogRedirect,
  generalErrorDialog,
  surveyDialog
} from '../../shared/dialogConfigs';
import ErrorBoundary from '../../shared/ErrorBoundary';
import { languageSelection } from '../../shared/translationConfig/helper';
import { withStore } from '../../store';
import { clearAllAttachments } from '../../store/attachments';
import { openDialog } from '../../store/dialog';
import {
  customerRemainingRequests,
  multipleRequests
} from '../../store/multiRequest';
import { singleCustomer } from '../../store/singleRecord';
import { logoutAndRedirectToLogin } from '../../utils/auth/logout';
import { filterValidRequests } from '../../utils/requestFilterHelper';
import { updateSessionStorage } from '../../utils/storage';
import timer from '../../utils/timer';
import Template from '../Template';
import { PAGE_NAME } from './constant';

import './index.css';

/* ----------------------------------
   Styled Components
---------------------------------- */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  max-width: 850px;
  padding: var(--spacing-xl, 24px) var(--spacing-lg, 16px);
  gap: var(--padding-base, 8px);
  background-color: var(--brand-white);
  border-radius: var(--corner-radius-base, 8px);

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const Subtitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  margin-right: var(--spacing-lg);
`;

const ActiveRequest = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: var(--spacing-xl);
  margin-bottom: var(--spacing-4xl);
`;
interface Props extends RouteComponentProps {
  store: any;
  dispatch: any;
  apiService: any;
}

const monthArray = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function getProcessingInfoSubtitle(
  t: any,
  isReqCancelled: boolean,
  requests: any[]
): JSX.Element {
  return (
    <Subtitle>
      <Typography>
        {t(
          isReqCancelled
            ? 'tracker.cancelledRequest.subtitleLine1'
            : 'tracker.processingInfo.subtitleLine1'
        )}
      </Typography>

      {!isReqCancelled && requests.length > 0 && (
        <Typography>
          {t('tracker.processingInfo.subtitleLine2')}
        </Typography>
      )}
    </Subtitle>
  );
}

export function translateDate(t: any, date = '') {
  const month = monthArray.find(m => date.includes(m));
  return month
    ? date.replace(month, t(`months.${month}`))
    : date;
}

function handleError(e: any, history: any, dispatch: any) {
  const errorDialog = errorDialogRedirect(history, '/');

  if (e?.message?.includes('404')) {
    dispatch(openDialog(errorDialogNotFound(history)));
  } else {
    dispatch(openDialog(errorDialog));
  }
}
export function CustomerPendingRequests({
  requestItem,
  apiService,
  dispatch,
  history,
  requests,
  last,
  index
}: any): JSX.Element {

  const existingHcId = updateSessionStorage.getItem('hcId');
  const { t } = useTranslation();

  const startOnboardingForm = async (hcId: string) => {
    if (existingHcId !== hcId && hcId) {
      updateSessionStorage.setItem('hcId', hcId);
      await apiService.getCustomerDetails(dispatch);
    }

    if (requests.length === 1) {
      dispatch(singleCustomer(false));
    }

    dispatch(clearAllAttachments());
    history.push('/secure/form');
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 'var(--spacing-lg)',
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}
      >
        <Typography sx={{ font: 'var(--sprout-font-body1-bold)' }}>
          {`ID: ${requestItem.id} -`}
        </Typography>

        <Typography
          component="span"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {requestItem.recordType}
          <InfoTooltip text={t('tracker.activeRequests.tooltipTitle')} />
        </Typography>

        <Button
          variant="text"
          onClick={() => startOnboardingForm(requestItem.id)}
          sx={{ display: { xs: 'none', sm: 'inline-flex' }, ml: 'auto' }}
        >
          {t('dashboard.actions.start', 'Start')}
        </Button>

        <Button
          variant="contained"
          onClick={() => startOnboardingForm(requestItem.id)}
          sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', mt: 2 }}
        >
          Start
        </Button>
      </Box>

      <Divider sx={{ mt: 2, mb: last === index ? 0 : 2 }} />
    </Box>
  );
}

function Component({
  store,
  apiService,
  dispatch,
  history
}: Props): JSX.Element {

  const {
    customer,
    customer: { expectedDate, status = '' } = {},
    multiRequest: { requests = [] } = {}
  } = store;

  const [emptyCustomerRequest, setemptyCustomerRequest] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const { t, i18n } = useTranslation();
  const { oktaAuth } = useOktaAuth();

  const isReqCancelled = status === 'cancelled';
  const { pathname } = history.location;

  useEffect(() => {
    const sessionLang = languageSelection();
    i18n.changeLanguage(sessionLang || 'en');
  }, [i18n, customer]);

  useEffect(() => {
    if (history.location.state && history.location.state.submitted) {
      history.replace(history.location.pathname, {});
      dispatch(openDialog(surveyDialog()));
    }
  }, [dispatch, history, history.location.state, requests]);

  const activeCustomerRequestCheck = async () => {
    if (emptyCustomerRequest) {
      setIsLoading(true);

      await apiService
        .getActiveRequests(dispatch)
        .then((response: any) => {
          logger.info(
            'RED-CARPET-FE Tracker Component getActiveRequests API response successfully.'
          );

          const { requests } = response;
          let filteredRequests = filterValidRequests(requests);

          dispatch(multipleRequests(filteredRequests));
          dispatch(customerRemainingRequests(filteredRequests));
          setemptyCustomerRequest(false);
        })
        .catch((e: any) => {
          logger.error(
            'Red-Carpet-FE-getActiveRequests-API - FAILED in Tracker Component reason',
            { e }
          );

          handleError(e, history, dispatch);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      if (!customer) {
        setIsLoading(true);

        apiService
          .getCustomer(dispatch)
          .catch((e: any) => {
            logger.error(
              'Red-Carpet-FE-getCustomer-API - FAILED in Tracker Component reason',
              { e }
            );

            dispatch(openDialog(generalErrorDialog()));
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  };

  useEffect(() => {
    activeCustomerRequestCheck();

    const cleanup = timer(
      () =>
        apiService.revokeToken().then(() => {
          logger.info(
            'Red-Carpet-FE Tracker component If Customer is inactive for more then 30 mins, Logging out from the session.'
          );
          logoutAndRedirectToLogin(oktaAuth);
        }),
      30,
      'min'
    );

    return cleanup;
  }, [apiService, history, dispatch, i18n, oktaAuth, customer, requests.length]);

  useEffect(() => {
    dispatch(customerRemainingRequests(requests));
  }, []);

  const goBackToFibiDashboard = () => {
    let host = sessionStorage.getItem('fibiHost');
    let path = sessionStorage.getItem('fibiPath');

    if (!host || !path) return;

    host = host.replace(/['"]+/g, '');
    path = path.replace(/['"]+/g, '');

    window.location.href = `${host}${path}/dashboard`;
  };

  return (
    <Template>
      {isLoading ? (
        <SplashLoader />
      ) : (
        <Wrapper>
          <ErrorBoundary componentTitle={PAGE_NAME}>
            <Dialog />

            <Header
              isOnlyUtility
              pathname={pathname}
            />

            {customer && (
              <ContentWrapper>
                <FormCard
                  title={
                    isReqCancelled
                      ? t('tracker.cancelledRequest.cancelledTitle')
                      : t('tracker.processingInfo.inProgressTitle')
                  }
                  subtitle={getProcessingInfoSubtitle(t, isReqCancelled, requests)}
                  image={
                    isReqCancelled
                      ? '/food/onboarding/assets/Cancelled_Request.svg'
                      : '/food/onboarding/assets/Status_Tracker.svg'
                  }
                >
                  {requests.length > 0 && (
                    <ActiveRequest>
                      <Typography>
                        {t('tracker.activeRequests.remainingRequestTitle')}
                      </Typography>

                      <Divider sx={{ mt: 2, mb: 2 }} />

                      {requests.map((req: any, index: number) => (
                        <CustomerPendingRequests
                          key={req.id}
                          requestItem={req}
                          requests={requests}
                          {...{ apiService, dispatch, history }}
                          last={requests.length - 1}
                          index={index}
                        />
                      ))}
                    </ActiveRequest>
                  )}

                  {!isReqCancelled && requests.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        gap: 'var(--spacing-lg)',
                        width: '100%',
                        alignSelf: 'flex-start',
                        textAlign: 'left',
                        marginBottom: 'var(--spacing-4xl)'
                      }}
                    >
                      <CheckCircle
                        sx={{
                          color: 'var(--brand-leaf-green)',
                          mt: '2px'
                        }}
                      />

                      <Typography sx={{ font: 'var(--sprout-font-h5)' }}>
                        {`${t('tracker.tracker.inProgressTitle')} ${translateDate(t, expectedDate)}`}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    sx={{
                      width: { xs: '100%', sm: 'auto' }
                    }}
                    variant="contained"
                    onClick={goBackToFibiDashboard}
                  >
                    {t('buttons.goBackToDashboard')}
                  </Button>
                </FormCard>
              </ContentWrapper>
            )}
          </ErrorBoundary>
        </Wrapper>
      )}
    </Template>
  );
}

export default withStore(withAPIService(withRouter(Component)));